import { Injectable, inject } from '@angular/core';

import { AppLockConfiguration, CategoryAppLockConfigurationService } from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, from } from 'rxjs';
import { filter, first, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { LocationsRouterStoreSelectors } from '../router';
import { getAncestors } from '../router/category';
import {
  AppLockApiActions,
  CategoryAppLockResolverActions,
  CategoryAppLockViewActions,
} from './category-app-lock.actions';
import { AppLock, AppLocks } from './category-app-lock.state';

@Injectable()
export class AppLockEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly categoryAppLockApi = inject(CategoryAppLockConfigurationService);

  /**
   * App lock has been selected for a category.
   * Effect: we add the app lock to the category via the API.
   */
  appLockSelectedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockSelected),
      mergeMap((action) => {
        return this.store.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          first(),
          mergeMap((category) => {
            if (!category) {
              return EMPTY;
            }

            const categoryType = category.categoryType;

            const requestPayload: AppLockConfiguration = {
              bundleIdentifier: action.bundleIdentifier,
              models: action.modelNames,
              appLockState: action.deviceState ?? 'None',
            };

            return safeMap(
              this.categoryAppLockApi.addAppLockToCategory({
                categoryType,
                categoryId: category.categoryId,
                appLockConfiguration: requestPayload,
              }),
              (appLockConfig) => {
                const appLocks: AppLocks = {
                  bundleIdentifier: action.bundleIdentifier,
                  modelNames: Array.from(appLockConfig.models),
                  appLockState: action.deviceState,
                  categoryId: category.categoryId,
                  categoryType: category.categoryType,
                };
                return AppLockApiActions.appLockSet({ category, appLocks });
              },
              (error) =>
                AppLockApiActions.appLockSetError({
                  error,
                })
            );
          })
        );
      })
    );
  });

  /**
   * App lock has been removed from a category.
   * Effect: we remove the app lock from the category via the API.
   */
  appLockRemovedFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockViewActions.appLockRemoved),
      mergeMap((action) => {
        return this.store.select(LocationsRouterStoreSelectors.selectCategory(action.categoryType)).pipe(
          first(),
          mergeMap((category) => {
            if (!category) {
              return EMPTY;
            }

            const categoryType = category.categoryType;

            return safeMap(
              this.categoryAppLockApi.deleteAppLockFromCategory({
                categoryType,
                categoryId: category.categoryId,
                state: action.deviceState,
                models: action.modelNames,
              }),
              () => {
                const appLocks: AppLocks = {
                  bundleIdentifier: action.bundleIdentifier,
                  modelNames: action.modelNames,
                  appLockState: action.deviceState,
                  categoryId: category.categoryId,
                  categoryType: category.categoryType,
                };
                return AppLockApiActions.appLockRemoved({ category, appLocks });
              },
              (error) =>
                AppLockApiActions.appLockRemovedError({
                  error,
                })
            );
          })
        );
      })
    );
  });

  /**
   * App locks have been requested for a category (resolver).
   * Effect: we load the app locks from the API.
   */
  appLocksForCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAppLockResolverActions.categoryAppLockRequested),
      filter((action) => !!action.category),
      mergeMap((action) => {
        const ancestors = getAncestors(action.category);
        return from(
          ancestors.map((ancestor) => {
            const categoryType = ancestor.categoryType;

            return safeMap(
              this.categoryAppLockApi.getAppLocksForCategory({
                categoryType,
                categoryId: ancestor.categoryId,
                models: action.modelNames,
              }),
              (locks) => {
                const appLocks = locks.flatMap((lock) => {
                  return Array.from(lock.models).map((lockModelName) => {
                    const appLock: AppLock = {
                      bundleIdentifier: lock.bundleIdentifier,
                      modelName: lockModelName,
                      appLockState: lock.appLockState,
                      categoryId: ancestor.categoryId,
                      categoryType: ancestor.categoryType,
                    };
                    return appLock;
                  });
                });
                return AppLockApiActions.appLockLoaded({ category: ancestor, appLocks });
              },
              (error) => AppLockApiActions.appLockLoadedError({ error })
            );
          })
        );
      }),
      mergeMap((actions) => actions)
    );
  });
}
